import type { ModerationScores } from "@/src/pages/Posts/posts.type";

/**
 * Lecture de la réponse brute du classifieur.
 *
 * Fonctions **pures**, testées : le contrat déclare `moderationScores` en
 * `unknown` — c'est un `jsonb` dont la forme appartient au service de
 * modération, pas à l'API. On la relit donc avec des gardes plutôt qu'en la
 * castant : un `as` mentirait le jour où le format évolue, et l'écran
 * afficherait `undefined` sans que rien ne le signale.
 */

function estObjet(valeur: unknown): valeur is Record<string, unknown> {
  return (
    typeof valeur === "object" && valeur !== null && !Array.isArray(valeur)
  );
}

/** `null` quand la valeur n'a pas la forme attendue. */
export function parseModerationScores(
  valeur: unknown,
): ModerationScores | null {
  if (!estObjet(valeur)) return null;

  const scores: ModerationScores = {};

  if (typeof valeur.model === "string") scores.model = valeur.model;
  if (typeof valeur.threshold === "number") scores.threshold = valeur.threshold;

  if (
    Array.isArray(valeur.evaluatedCategories) &&
    valeur.evaluatedCategories.every((c) => typeof c === "string")
  ) {
    scores.evaluatedCategories = valeur.evaluatedCategories;
  }

  // ⚠️ `null` est une valeur SIGNIFIANTE ici — « non jugé » —, pas une absence.
  // La distinguer de `undefined` est tout l'intérêt de ce champ.
  if (valeur.highestScore === null || typeof valeur.highestScore === "number") {
    scores.highestScore = valeur.highestScore;
  }

  if (Array.isArray(valeur.perPhoto)) {
    scores.perPhoto = valeur.perPhoto.map((photo) =>
      estObjet(photo)
        ? Object.fromEntries(
            Object.entries(photo).filter(
              ([, score]) => typeof score === "number",
            ),
          )
        : null,
    ) as (Record<string, number> | null)[];
  }

  return scores;
}

export type VerdictKind = "unjudged" | "clean" | "flagged";

/**
 * Ce que les scores disent, en un mot.
 *
 * ⚠️ **`unjudged` n'est pas `clean`.** Classifieur injoignable, réponse
 * illisible, délai dépassé : la publication attend parce qu'on n'a **pas pu**
 * la regarder. Confondre les deux ferait refuser par réflexe une image
 * parfaitement valide — c'est précisément le cas que l'écran doit distinguer.
 */
export function verdictOf(scores: ModerationScores | null): VerdictKind {
  if (!scores || scores.highestScore === undefined) return "unjudged";
  if (scores.highestScore === null) return "unjudged";

  const seuil = scores.threshold ?? 0.5;

  return scores.highestScore < seuil ? "clean" : "flagged";
}

/**
 * La catégorie qui a déclenché le score le plus haut, toutes photos confondues.
 *
 * `null` quand rien n'a été jugé, ou quand aucune catégorie ne ressort — c'est
 * ce qui évite d'afficher « sexual 0 % » sur une publication propre.
 */
export function topCategory(scores: ModerationScores | null): {
  category: string;
  score: number;
} | null {
  if (!scores?.perPhoto?.length) return null;

  let meilleure: { category: string; score: number } | null = null;

  for (const photo of scores.perPhoto) {
    if (!photo) continue;

    for (const [category, score] of Object.entries(photo)) {
      if (score > (meilleure?.score ?? 0)) meilleure = { category, score };
    }
  }

  return meilleure;
}

/** `0.6234` → `62 %`. Arrondi à l'entier : la précision n'apporte rien à l'œil. */
export function formatScore(score: number, locale: string): string {
  return new Intl.NumberFormat(locale, {
    style: "percent",
    maximumFractionDigits: 0,
  }).format(score);
}
